import { get } from '@vueuse/core'
import { defineStore } from 'pinia'
import { usePharmacyPageCases } from './use-pharmacy-page-cases'
import type { NavigateWithQueryCallback } from '@/models/apofinder'
import { PageType } from '@/models/pages'
import type { PharmacyDetails } from '@/models/pharmacy-details'
import type { PharmacyAction, PharmacySwitchCallback, PharmacySwitchCallbacks, PharmacySwitchCheckPayload, PharmacySwitchPharmacies } from '@/models/pharmacy-switch'

// should only be called on client side
export const usePharmacySwitchStore = defineStore('pharmacy-switch', () => {
  const userStore = useUserStore()
  const authStore = useAuthStore()
  const currentPharmacyStore = useCurrentPharmacyStore()
  const modals = useModalManager()

  // Pharmacy choices
  const $selectedPharmacy = ref<PharmacyDetails>()
  const $newPharmacy = ref<PharmacyDetails>()

  // Actions
  const $onKeep = ref<PharmacySwitchCallback>()
  const $onSwitch = ref<PharmacySwitchCallback>()

  // Loading states for keep and switch buttons
  const onKeepLoading = ref(false)
  const onSwitchLoading = ref(false)
  const isLoading = computed(() => get(onKeepLoading) || get(onSwitchLoading))

  const hasDifferentPharmaciesAfterLogin = ref(false)
  const setHasDifferentPharmaciesAfterLogin = (value: boolean) => {
    hasDifferentPharmaciesAfterLogin.value = value
  }

  const startLoading = (action: PharmacyAction) => {
    set(onKeepLoading, action === 'keep')
    set(onSwitchLoading, action === 'switch')
  }

  const stopLoading = () => {
    set(onKeepLoading, false)
    set(onSwitchLoading, false)
  }

  const setPharmacies = ({ selectedPharmacy, newPharmacy }: PharmacySwitchPharmacies) => {
    set($selectedPharmacy, selectedPharmacy)
    set($newPharmacy, newPharmacy)
  }

  const setActions = ({ onKeep, onSwitch }: PharmacySwitchCallbacks) => {
    set($onKeep, onKeep)
    set($onSwitch, onSwitch)
  }

  const clear = () => {
    setPharmacies({ selectedPharmacy: null, newPharmacy: null })
    setActions({ onKeep: undefined, onSwitch: undefined })
    setHasDifferentPharmaciesAfterLogin(false)
    stopLoading()
  }

  /**
   * Performs final onKeep/onSwitch actions when pharmacy is selected via pharmacy-switch modal,
   * or when automatically selected via pharmacy switch check
   * (when user does not have any pharmacy selected, but lands on pharmacy-related page
   */
  const onPharmacyChoice = async (action: PharmacyAction) => {
    startLoading(action)

    const pharmacyChoiceAction = get(action === 'keep' ? $onKeep : $onSwitch)
    const pharmacy = get(action === 'keep' ? $selectedPharmacy : $newPharmacy)!

    await ensureCookieAndUserPharmacy(pharmacy.id)

    // TODO: because of full page refresh navigation, only one of these below are run (if it performs navigation)

    // for pharmacy and product pages -> reads pharmacy from url, builds url and navigates to it
    // do we really need it?
    ensurePharmacyPageNavigation(pharmacy)

    // apofinder
    await pharmacyChoiceAction?.(pharmacy)

    stopLoading()
  }

  /**
   * Performed only on the client
   */
  const performPharmacyCheck = async ({
    selectedPharmacyId,
    newPharmacyId,
    onKeep,
    onSwitch,
    onEqual,
    fetchedPharmacy,
  }: PharmacySwitchCheckPayload) => {
    if (!isClient)
      return

    const { mismatch, newPharmacy, selectedPharmacy } = await checkMismatchAndLoadPharmacies({ selectedPharmacyId, newPharmacyId }, fetchedPharmacy)

    onShowLogs(() => console.log('Perform pharmacy check', {
      fetchedPharmacy,
      selectedPharmacyId,
      newPharmacyId,
      $selectedPharmacy: $selectedPharmacy.value,
      $newPharmacy: $newPharmacy.value,
      mismatch,
      selectedPharmacy,
      newPharmacy,
    }))

    if (!mismatch) {
      onShowLogs(() => console.log('Pharmacies are equal. No need to switch'))
      onEqual?.()
      clear()
      return
    }

    setPharmacies({ selectedPharmacy, newPharmacy })

    if (selectedPharmacy && newPharmacy) {
      onShowLogs(() => console.log('Pharmacies are fetched and different. Launching pharmacy switch modal'))
      setActions({ onKeep, onSwitch })
      modals.show('PharmacySwitch')
    } else if (selectedPharmacy) {
      onShowLogs(() => console.log('Selected pharmacy is fetched, new pharmacy is not'))
      setActions({ onKeep })
      onPharmacyChoice('keep')
    } else if (newPharmacy) {
      onShowLogs(() => console.log('New pharmacy is fetched, selected pharmacy is not'))
      setActions({ onSwitch })
      onPharmacyChoice('switch')
    } else {
      onShowLogs(() => console.log('Pharmacies are not fetched'))
      onEqual?.()
      clear()
    }
  }

  const fromApofinder = async (
    pharmacyId: number,
    customCallback: {
      onKeep?: (cb: NavigateWithQueryCallback, pharmacy: PharmacyDetails) => void
      onSwitch?: (cb: NavigateWithQueryCallback, pharmacy: PharmacyDetails) => void
    } = {},
  ) => {
    const response = await fetchPharmacyDetails(pharmacyId)
    // onKeep and onSwitch actions are passed as arguments, and will be stored in the store to be executed later
    await performPharmacyCheck({
      selectedPharmacyId: userStore.getPharmacyCookie(),
      newPharmacyId: pharmacyId,
      // Pharmacy details
      fetchedPharmacy: response.data,
      // Actions
      onKeep(pharmacy: PharmacyDetails) {
        const navigate = (query?: Record<string, string>) => navigateToPharmacyPage(pharmacy, query)
        // If custom onKeep callback was provided, execute it
        if (customCallback.onKeep) {
          customCallback.onKeep(navigate, pharmacy)
        } else {
          navigate()
        }
      },
      onSwitch(pharmacy: PharmacyDetails) {
        const navigate = (query?: Record<string, string>) => navigateToPharmacyPage(pharmacy, query)
        // If custom onSwitch callback was provided, execute it
        if (customCallback.onSwitch) {
          customCallback.onSwitch(navigate, pharmacy)
        } else {
          navigate()
        }
      },
    })
  }

  const fromWpid = (wpid: number) => performPharmacyCheck({
    selectedPharmacyId: userStore.getPharmacyCookie(),
    newPharmacyId: wpid,
    onKeep(_pharmacy: PharmacyDetails) {
      // empty
    },
    onSwitch(pharmacy: PharmacyDetails) {
      currentPharmacyStore.ensure(pharmacy.id)
    },
  })

  const fromPharmacyPage = async (urlPharmacyId: number) => {
    const cookie = userStore.getPharmacyCookie()
    const { cookieVsUrl, urlAndUserVsCookie, userVsUrl } = usePharmacyPageCases()
    const userPharmacyId = userStore.userPharmacyId

    if (!authStore.isAuthenticated || userPharmacyId === cookie || !userPharmacyId) {
      // If user has pharmacy, it's the same as cookie
      onShowLogs(() => console.log('Cookie vs Url'))
      return cookieVsUrl(urlPharmacyId)
    }

    if (userPharmacyId === urlPharmacyId) {
      // User pharmacy is the same as the url pharmacy
      onShowLogs(() => console.log('Url (same as user) vs Cookie'))
      return urlAndUserVsCookie(urlPharmacyId)
    }

    onShowLogs(() => console.log('User vs Url'))
    return userVsUrl(urlPharmacyId)
  }

  const fromLogin = (returnUrl?: string) => {
    const route = useRoute()

    const isCheckoutPage = route.meta.type === PageType.Checkout
    const isLoginPage = route.meta.type === PageType.Login
    const isProductPage = route.meta.type === PageType.Product

    const { pharmacyId: pharmacyIdString } = route.params as { pharmacyId?: string }

    returnUrl = validateReturnUrl(returnUrl)

    // is pharmacy page
    if (pharmacyIdString) {
      const urlPharmacyId = parseInt(pharmacyIdString, 10)

      if (!Number.isNaN(urlPharmacyId))
        return fromPharmacyPage(urlPharmacyId)
    }

    if (isProductPage)
      return

    if (isCheckoutPage)
      return

    if (isLoginPage && authStore.isAuthenticated) {
      return performPharmacyCheck({
        selectedPharmacyId: userStore.userPharmacyId,
        newPharmacyId: userStore.getPharmacyCookie(),
        onKeep(pharmacy: PharmacyDetails) {
          if (returnUrl)
            customNavigateTo(returnUrl)
          else
            navigateToPharmacyPage(pharmacy)
        },
        onSwitch(pharmacy: PharmacyDetails) {
          if (returnUrl)
            customNavigateTo(returnUrl)
          else
            navigateToPharmacyPage(pharmacy)
        },
        onEqual: async () => {
          if (userStore.userPharmacyId) {
            const { data: pharmacy } = await currentPharmacyStore.ensure(userStore.userPharmacyId)
            if (pharmacy && userStore.user) {
              await ensureCookieAndUserPharmacy(pharmacy.id)
              if (returnUrl) {
                customNavigateTo(returnUrl)
              }
              else {
                customNavigateTo({
                  name: 'account-userId',
                  params: {
                    userId: userStore.user.id,
                  },
                })
              }
            }
          }
        },
      })
    }

    // is any other page, logged in via login modal
    return performPharmacyCheck({
      selectedPharmacyId: userStore.userPharmacyId,
      newPharmacyId: userStore.getPharmacyCookie(),
      onKeep(pharmacy: PharmacyDetails) {
        navigateToPharmacyPage(pharmacy)
      },
      onSwitch(pharmacy: PharmacyDetails) {
        navigateToPharmacyPage(pharmacy)
      },
      onEqual: async () => {
        if (userStore.userPharmacyId) {
          const { data: pharmacy } = await currentPharmacyStore.ensure(userStore.userPharmacyId)
          if (pharmacy) {
            await ensureCookieAndUserPharmacy(pharmacy.id)
            await navigateToPharmacyPage(pharmacy, undefined, route.meta.fullPharmacySlug ? route.fullPath : undefined)
          }
        }
      },
    })
  }

  return {
    // Pharmacies state and its actions
    selectedPharmacy: $selectedPharmacy,
    newPharmacy: $newPharmacy,
    clear,
    // Loading
    onKeepLoading,
    onSwitchLoading,
    isLoading,
    // Switch choice actions
    onPharmacyChoice,
    // Pharmacy switch check
    performPharmacyCheck,
    // Custom switch actions
    fromLogin,
    fromApofinder,
    fromWpid,
    fromPharmacyPage,
    hasDifferentPharmaciesAfterLogin,
    setHasDifferentPharmaciesAfterLogin,
  }
})
